import type { FileType } from '@/types/file-upload'

export type Confidence = 'high' | 'medium' | 'low'

/** The user-facing types offered in both upload modals' dropdowns. */
export const SELECTABLE_FILE_TYPES = [
  'pnid',
  'pfd',
  'document',
  'timeseries',
  'plc_program',
] as const
export type SelectableFileType = (typeof SELECTABLE_FILE_TYPES)[number]

/** Map internal FileType to the user-facing dropdown types. */
export function toSelectableFileType(ft: FileType): SelectableFileType {
  switch (ft) {
    case 'pnid':
    case 'pfd':
    case 'timeseries':
    case 'plc_program':
      return ft
    default:
      return 'document'
  }
}

export interface FileTypeDetection {
  fileName: string
  suggestedType: FileType
  confidence: Confidence
  alternatives: FileType[]
}

export interface BatchDetectionResult {
  files: FileTypeDetection[]
  isHomogeneous: boolean
  dominantType: FileType | null
}

/** Filename patterns that hint at a specific file type. First match wins.
 *  Word boundaries (\b) prevent false positives like "rapid" matching "pid". */
const FILENAME_HINTS: { pattern: RegExp; type: FileType }[] = [
  { pattern: /\bp[&\-_\s]?id\b|pnid/i, type: 'pnid' },
  { pattern: /\bpfd\b|process[_\s-]?flow/i, type: 'pfd' },
  { pattern: /time[_\s-]?series/i, type: 'timeseries' },
  { pattern: /data[_\s-]?sheet/i, type: 'datasheet' },
]

interface ExtensionRule {
  suggestedType: FileType
  confidence: Confidence
  alternatives: FileType[]
}

/** Static rules for unambiguous extensions (no filename hints needed). */
const UNAMBIGUOUS_EXTENSIONS: Record<string, ExtensionRule> = {
  docx: { suggestedType: 'document', confidence: 'high', alternatives: [] },
  pptx: { suggestedType: 'document', confidence: 'high', alternatives: [] },
  txt:  { suggestedType: 'document', confidence: 'high', alternatives: [] },
  md:   { suggestedType: 'document', confidence: 'high', alternatives: [] },
  dwg:  { suggestedType: 'pnid',    confidence: 'high', alternatives: [] },
  dxf:  { suggestedType: 'pnid',    confidence: 'high', alternatives: [] },
  json: { suggestedType: 'json',    confidence: 'high', alternatives: [] },
  mp3:  { suggestedType: 'audio',   confidence: 'high', alternatives: [] },
  wav:  { suggestedType: 'audio',   confidence: 'high', alternatives: [] },
  m4a:  { suggestedType: 'audio',   confidence: 'high', alternatives: [] },
  // PLC program exports (VISN-305) — vendor-specific, no other reading.
  tcpou: { suggestedType: 'plc_program', confidence: 'high', alternatives: [] },
  st:    { suggestedType: 'plc_program', confidence: 'high', alternatives: [] },
  scl:   { suggestedType: 'plc_program', confidence: 'high', alternatives: [] },
  l5x:   { suggestedType: 'plc_program', confidence: 'high', alternatives: [] },
}

/** Extensions whose type depends on filename hints. */
const AMBIGUOUS_DEFAULTS: Record<string, ExtensionRule> = {
  pdf:  { suggestedType: 'document',   confidence: 'low',    alternatives: ['pnid', 'pfd'] },
  png:  { suggestedType: 'pnid',       confidence: 'medium', alternatives: ['pfd'] },
  jpg:  { suggestedType: 'pnid',       confidence: 'medium', alternatives: ['pfd'] },
  jpeg: { suggestedType: 'pnid',       confidence: 'medium', alternatives: ['pfd'] },
  tiff: { suggestedType: 'pnid',       confidence: 'medium', alternatives: ['pfd'] },
  tif:  { suggestedType: 'pnid',       confidence: 'medium', alternatives: ['pfd'] },
  xlsx: { suggestedType: 'document',   confidence: 'low',    alternatives: ['timeseries'] },
  xls:  { suggestedType: 'document',   confidence: 'low',    alternatives: ['timeseries'] },
  csv:  { suggestedType: 'timeseries', confidence: 'low',    alternatives: ['document'] },
  // Generic XML could be anything; PLC XML is chosen via the dropdown, never auto-routed.
  xml:  { suggestedType: 'document',   confidence: 'low',    alternatives: ['plc_program'] },
}

function matchFilenameHint(fileName: string): FileType | null {
  const nameWithoutExt = fileName.replace(/\.[^.]+$/, '')
  for (const { pattern, type } of FILENAME_HINTS) {
    if (pattern.test(nameWithoutExt)) return type
  }
  return null
}

function getExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? ''
}

/** Detect file type for a single file. */
export function detectFileType(fileName: string): FileTypeDetection {
  const ext = getExtension(fileName)

  // 1. Unambiguous extension — return immediately
  const unambiguous = UNAMBIGUOUS_EXTENSIONS[ext]
  if (unambiguous) {
    return { fileName, ...unambiguous }
  }

  // 2. Ambiguous extension — check filename hints
  const ambiguous = AMBIGUOUS_DEFAULTS[ext]
  if (ambiguous) {
    const hint = matchFilenameHint(fileName)
    if (hint) {
      const alternatives = [ambiguous.suggestedType, ...ambiguous.alternatives]
        .filter(t => t !== hint)
      return { fileName, suggestedType: hint, confidence: 'high', alternatives }
    }
    return { fileName, ...ambiguous }
  }

  // 3. Unknown extension — default to document
  return { fileName, suggestedType: 'document', confidence: 'low', alternatives: [] }
}

/** Compute the batch summary for a set of per-file detections. */
export function summarizeBatch(detections: FileTypeDetection[]): BatchDetectionResult {
  const types = new Set(detections.map(d => d.suggestedType))

  // Find the most frequent suggested type
  let dominantType: FileType | null = null
  if (detections.length > 0) {
    const counts = new Map<FileType, number>()
    for (const d of detections) {
      counts.set(d.suggestedType, (counts.get(d.suggestedType) ?? 0) + 1)
    }
    let maxCount = 0
    for (const [type, count] of counts) {
      if (count > maxCount) {
        maxCount = count
        dominantType = type
      }
    }
  }

  return {
    files: detections,
    isHomogeneous: types.size <= 1,
    dominantType,
  }
}

/** Detect file types for a batch and compute summary. */
export function detectBatchFileTypes(files: File[]): BatchDetectionResult {
  return summarizeBatch(files.map(f => detectFileType(f.name)))
}
